/**
 * Splash Screen URL Mode
 * 
 * Animated wave animation that reveals "PIVOT" text in the URL hash.
 * Three waves travel from left to right, revealing characters one by one.
 */

import {
  createPhysicsSplashScreenState,
  generatePhysicsSplashScreen,
  type PhysicsSplashScreenState,
} from './splash-screen-physics.js';
import {
  createCipherSplashScreenState,
  generateCipherSplashScreen,
  type CipherSplashScreenState,
} from './splash-screen-cipher.js';

export type SplashScreenAnimationType = 'wave' | 'physics' | 'cipher';

/**
 * Shared constants for splash screen animations
 */
export const SPLASH_SCREEN_TEXT = 'pivot (alpha) 0.0.1';
export const SPLASH_SCREEN_LENGTH = 40;
export const SPLASH_SCREEN_BLOCKS = ['▁', '▂', '▃', '▄', '▅', '▆', '▇', '█'];
export const SPLASH_SCREEN_SMALL_BLOCK = SPLASH_SCREEN_BLOCKS[0]; // '▁'

export interface SplashScreenState {
  active: boolean;
  startTime: number;
  lastUrlUpdate: number;
  urlUpdateIntervalId: ReturnType<typeof setInterval> | null;
  animationType: SplashScreenAnimationType;
  physicsState?: PhysicsSplashScreenState;
  cipherState?: CipherSplashScreenState;
}

/**
 * Available splash screen animation types
 */
const SPLASH_ANIMATION_TYPES: SplashScreenAnimationType[] = ['wave', 'physics', 'cipher'];

/**
 * Randomly select a splash screen animation type
 */
export function selectRandomSplashAnimationType(): SplashScreenAnimationType {
  const randomIndex = Math.floor(Math.random() * SPLASH_ANIMATION_TYPES.length);
  return SPLASH_ANIMATION_TYPES[randomIndex];
}

/**
 * Create splash screen state
 */
export function createSplashScreenState(
  animationType?: SplashScreenAnimationType
): SplashScreenState {
  const type = animationType || selectRandomSplashAnimationType();
  
  const state: SplashScreenState = {
    active: true,
    startTime: performance.now(),
    lastUrlUpdate: performance.now(),
    urlUpdateIntervalId: null,
    animationType: type,
  };

  // Initialize physics state if using physics animation
  if (type === 'physics') {
    state.physicsState = createPhysicsSplashScreenState();
  }

  // Initialize cipher state if using cipher animation
  if (type === 'cipher') {
    state.cipherState = createCipherSplashScreenState();
  }

  return state;
}

/**
 * Generate splash screen string based on animation type
 */
export function generateSplashScreen(state: SplashScreenState): string {
  if (!state.active) {
    return '';
  }

  if (state.animationType === 'physics' && state.physicsState) {
    const result = generatePhysicsSplashScreen(state.physicsState);
    // Sync active state from physics state
    if (!state.physicsState.active) {
      state.active = false;
    }
    return result;
  }

  if (state.animationType === 'cipher' && state.cipherState) {
    const result = generateCipherSplashScreen(state.cipherState);
    // Sync active state from cipher state
    if (!state.cipherState.active) {
      state.active = false;
    }
    return result;
  }
  
  // Default to wave animation
  return generateWaveSplashScreen(state);
}

/**
 * Generate splash screen string with wave animation
 */
function generateWaveSplashScreen(state: SplashScreenState): string {
  if (!state.active) {
    return '';
  }

  const elapsed = performance.now() - state.startTime;
  const animationDuration = 2000; // 2 seconds for wave animation
  const totalSplashDuration = 10000; // 10 seconds total

  // Use a shorter fixed length for splash screen to avoid URL length issues
  const length = 80;

  // After animation ends, return empty string to signal completion
  if (elapsed >= totalSplashDuration) {
    state.active = false;
    return ''; // Return empty to end splash screen
  }

  const blocks = ['▁', '▂', '▃', '▄', '▅', '▆', '▇', '█'];

  // Create a single wave cycle (lowest to tallest to lowest)
  // One complete cycle: 0→1→2→3→4→5→6→7→6→5→4→3→2→1→0 (15 characters)
  const waveCycle: number[] = [];
  for (let i = 0; i <= 7; i++) waveCycle.push(i);
  for (let i = 6; i >= 0; i--) waveCycle.push(i);
  const waveWidth = waveCycle.length; // 15 characters

  // After animation completes, show static state with "PIVOT" revealed
  if (elapsed >= animationDuration) {
    const pivotStartPos = 20;
    const pivotText = 'PIVOT';
    const pivotEndPos = pivotStartPos + pivotText.length;

    // Build the string: small blocks before, "PIVOT" in the middle, small blocks after
    let result = '';
    for (let i = 0; i < length; i++) {
      if (i >= pivotStartPos && i < pivotEndPos) {
        result += pivotText[i - pivotStartPos];
      } else {
        result += blocks[0];
      }
    }

    document.title = result;
    return result;
  }

  // Animation phase (0-2 seconds): three waves traveling left to right
  const progress = Math.min(elapsed / animationDuration, 1.0); // 0 to 1

  const pivotStartPos = 20;
  const pivotText = 'PIVOT';
  const pivotEndPos = pivotStartPos + pivotText.length;

  const waveSpacing = 15; // Space between wave peaks

  // Calculate how far waves should travel - LEFT TO RIGHT
  const startOffset = -waveWidth; // Start off-screen left
  const endTarget = pivotStartPos; // Third wave peak reaches here
  const totalTravel = endTarget - startOffset + waveSpacing * 2; // Total distance to travel

  // Calculate current positions - LEFT TO RIGHT
  const basePosition = startOffset + totalTravel * progress;
  const wave1PeakPos = basePosition;
  const wave2PeakPos = wave1PeakPos + waveSpacing;
  const wave3PeakPos = wave2PeakPos + waveSpacing;

  // Build the result string
  const result: string[] = new Array(length);

  // Initialize with smallest blocks
  for (let i = 0; i < length; i++) {
    result[i] = blocks[0];
  }

  // Draw the three waves (left to right)
  const waves = [
    { peakPos: wave1PeakPos, index: 0 },
    { peakPos: wave2PeakPos, index: 1 },
    { peakPos: wave3PeakPos, index: 2 }
  ];

  // Draw all three waves
  for (const wave of waves) {
    const peakPos = wave.peakPos;

    // Draw wave cycle centered at peak position
    for (let i = 0; i < waveWidth; i++) {
      const waveOffset = i - Math.floor(waveWidth / 2);
      const pos = Math.round(peakPos + waveOffset);

      // Only draw if within visible bounds
      if (pos >= 0 && pos < length) {
        const blockIndex = waveCycle[i];

        // Always draw wave blocks everywhere (including PIVOT area)
        // The text will be written last in STEP 3, so it will override
        result[pos] = blocks[blockIndex];
      }
    }
  }

  // Final pass: reveal "PIVOT" text when the first wave's peak passes (from left)
  // Reveal when wave1 (the leftmost, first wave) has clearly passed each position
  if (wave1PeakPos > pivotStartPos + 8) {
    for (let i = pivotStartPos; i < pivotEndPos && i < length; i++) {
      // Reveal character when wave1 peak has clearly passed this position
      if (wave1PeakPos > i + 8) {
        result[i] = pivotText[i - pivotStartPos];
      }
    }
  }

  const resultString = result.join('');
  document.title = resultString;
  return resultString;
}

